import { action, computed, makeObservable, observable } from 'mobx';

export const CHAT_MIN_WIDTH = 400;
export const BROWSER_MIN_WIDTH = 360;
export const BROWSER_DEFAULT_WIDTH = 480;

export interface DockMetrics {
  canDock: boolean;
  dockedWidth: number;
}

export function computeDock(
  availableWidth: number,
  requestedWidth: number,
  panelMinWidth: number
): DockMetrics {
  if (availableWidth <= 0) {
    return { canDock: true, dockedWidth: requestedWidth };
  }
  return {
    canDock: availableWidth >= CHAT_MIN_WIDTH + panelMinWidth,
    dockedWidth: Math.min(requestedWidth, availableWidth - CHAT_MIN_WIDTH),
  };
}

class ChatRowLayoutStore {
  rowWidth = 0;
  browserWidth = BROWSER_DEFAULT_WIDTH;
  isBrowserOpen = false;
  isBrowserDismissed = false;
  isBrowserFullscreen = false;

  constructor() {
    makeObservable(this, {
      rowWidth: observable,
      browserWidth: observable,
      isBrowserOpen: observable,
      isBrowserDismissed: observable,
      isBrowserFullscreen: observable,
      isBrowserVisible: computed,
      browserDock: computed,
      isBrowserFloating: computed,
      previewAvailableWidth: computed,
      setRowWidth: action,
      setBrowserWidth: action,
      setBrowserOpen: action,
      dismissBrowser: action,
      restoreBrowser: action,
      toggleBrowserFullscreen: action,
    });
  }

  get isBrowserVisible(): boolean {
    return this.isBrowserOpen && !this.isBrowserDismissed;
  }

  get browserDock(): DockMetrics {
    return computeDock(this.rowWidth, this.browserWidth, BROWSER_MIN_WIDTH);
  }

  get isBrowserFloating(): boolean {
    return this.isBrowserFullscreen || !this.browserDock.canDock;
  }

  get previewAvailableWidth(): number {
    if (this.rowWidth <= 0) return 0;
    const browserIsDocked = this.isBrowserVisible && !this.isBrowserFloating;
    return this.rowWidth - (browserIsDocked ? this.browserDock.dockedWidth : 0);
  }

  setRowWidth(width: number): void {
    this.rowWidth = width;
  }

  setBrowserWidth(width: number): void {
    this.browserWidth = Math.round(width);
  }

  setBrowserOpen(isOpen: boolean): void {
    this.isBrowserOpen = isOpen;
    if (!isOpen) {
      this.isBrowserFullscreen = false;
      this.isBrowserDismissed = false;
    }
  }

  dismissBrowser(): void {
    this.isBrowserDismissed = true;
    this.isBrowserFullscreen = false;
  }

  restoreBrowser(): void {
    this.isBrowserDismissed = false;
  }

  toggleBrowserFullscreen(): void {
    this.isBrowserFullscreen = !this.isBrowserFullscreen;
  }
}

export const chatRowLayoutStore = new ChatRowLayoutStore();
